<template>
  <section id="section-withdraw" class="wallet-section wallet-section--withdraw">
    <header class="wallet-section__header">
      <h2>{{ $t('wallet.withdrawTitle') }}</h2>
      <p>{{ withdrawSubtitle }}</p>
    </header>

    <nav
      v-if="showWalletTabs"
      class="wallet-tabs"
      role="tablist"
      :aria-label="$t('wallet.withdrawTabAria')"
    >
      <button
        v-if="showBankWalletTab"
        type="button"
        role="tab"
        class="wallet-tabs__btn"
        :class="{ 'wallet-tabs__btn--active': withdrawTab === 'bank' }"
        :aria-selected="withdrawTab === 'bank'"
        @click="setWithdrawTab('bank')"
      >
        <span class="wallet-tabs__icon" aria-hidden="true">{{ siteCurrency }}</span>
        {{ $t('wallet.withdrawTabBank') }}
      </button>
      <button
        v-if="showTetherWalletTab"
        type="button"
        role="tab"
        class="wallet-tabs__btn"
        :class="{ 'wallet-tabs__btn--active': withdrawTab === 'tether' }"
        :aria-selected="withdrawTab === 'tether'"
        @click="setWithdrawTab('tether')"
      >
        <span class="wallet-tabs__icon" aria-hidden="true">₮</span>
        {{ $t('wallet.withdrawTabTether') }}
      </button>
    </nav>

    <article v-show="withdrawTab === 'bank'" class="wallet-guide">
      <h3 class="wallet-guide__title">{{ $t('wallet.bankWithdrawGuideTitle') }}</h3>
      <div class="wallet-guide__body">
        <p>
          {{ $t('wallet.bankWithdrawGuideParagraph1') }}
        </p>
        <p>
          {{ $t('wallet.bankWithdrawGuideParagraph2Prefix') }}
          <button type="button" class="wallet-guide__link" @click="$emit('telegram')">
            {{ $t('nav.telegramInquiry') }}
          </button>
          {{ $t('wallet.bankWithdrawGuideParagraph2Suffix') }}
        </p>
      </div>
    </article>

    <article v-show="withdrawTab === 'tether'" class="wallet-guide">
      <h3 class="wallet-guide__title">{{ $t('wallet.tetherWithdrawGuideTitle') }}</h3>
      <div class="wallet-guide__body">
        <p>
          {{ $t('wallet.tetherWithdrawGuideParagraph1') }}
        </p>
        <p>
          {{ $t('wallet.tetherWithdrawGuideParagraph2Prefix') }}
          <button type="button" class="wallet-guide__link" @click="$emit('telegram')">
            {{ $t('nav.telegramInquiry') }}
          </button>
          {{ $t('wallet.tetherWithdrawGuideParagraph2Suffix') }}
        </p>
      </div>
    </article>

    <article class="wallet-panel">
      <h3 class="wallet-panel__title">
        {{ withdrawTab === 'bank' ? $t('wallet.bankWithdrawRequestTitle') : $t('wallet.tetherWithdrawRequestTitle') }}
      </h3>

      <p v-if="successMessage" class="wallet-panel__success">{{ successMessage }}</p>
      <p v-if="submitError" class="wallet-panel__error">{{ submitError }}</p>

      <form class="wallet-form" @submit.prevent="onSubmitWithdraw">
        <div class="wallet-form__field wallet-form__field--available">
          <span class="wallet-form__label">{{ $t('wallet.availableBalanceLabel') }}</span>
          <div class="wallet-form__available-row">
            <input
              type="text"
              readonly
              :value="availableBalanceDisplay"
              class="wallet-form__readonly"
            >
            <button
              type="button"
              class="wallet-form__available-check-btn"
              @click="openWithdrawAvailableModal"
            >
              {{ $t('wallet.checkAvailableBalance') }}
            </button>
          </div>
        </div>

        <div class="wallet-form__field wallet-form__field--amount">
          <span class="wallet-form__label">{{ $t('wallet.withdrawAmountLabel') }} <em>({{ $t('wallet.required') }})</em></span>
          <WalletUsdtQuote v-if="withdrawTab === 'tether'" :krw-amount="amount">
            <input
              v-model="amountDisplay"
              type="text"
              inputmode="numeric"
              class="wallet-form__amount-input"
              :placeholder="$t('wallet.amountPlaceholder')"
              autocomplete="off"
              @input="onAmountInput"
            >
          </WalletUsdtQuote>
          <input
            v-else
            v-model="amountDisplay"
            type="text"
            inputmode="numeric"
            class="wallet-form__amount-input"
            :placeholder="$t('wallet.amountPlaceholder')"
            autocomplete="off"
            @input="onAmountInput"
          >
        </div>

        <div class="wallet-form__quick">
          <button type="button" @click="addAmount(10000)">{{ $t('wallet.quick10k') }}</button>
          <button type="button" @click="addAmount(30000)">{{ $t('wallet.quick30k') }}</button>
          <button type="button" @click="addAmount(50000)">{{ $t('wallet.quick50k') }}</button>
          <button type="button" @click="addAmount(100000)">{{ $t('wallet.quick100k') }}</button>
          <button type="button" @click="addAmount(500000)">{{ $t('wallet.quick500k') }}</button>
          <button type="button" class="wallet-form__quick-reset" @click="resetAmount">{{ $t('wallet.reset') }}</button>
        </div>

        <template v-if="withdrawTab === 'bank'">
          <label class="wallet-form__field">
            <span class="wallet-form__label">{{ $t('wallet.bankNameLabel') }} <em>({{ $t('wallet.required') }})</em></span>
            <input
              v-model.trim="bankName"
              type="text"
              maxlength="40"
              :placeholder="$t('wallet.bankNamePlaceholder')"
              autocomplete="off"
            >
          </label>

          <label class="wallet-form__field">
            <span class="wallet-form__label">{{ $t('wallet.accountHolderLabel') }} <em>({{ $t('wallet.required') }})</em></span>
            <input
              v-model.trim="bankMaster"
              type="text"
              maxlength="40"
              :placeholder="$t('wallet.accountHolderPlaceholder')"
              autocomplete="name"
            >
          </label>

          <label class="wallet-form__field">
            <span class="wallet-form__label">{{ $t('wallet.accountNumberLabel') }} <em>({{ $t('wallet.required') }})</em></span>
            <input
              v-model.trim="bankAccount"
              type="text"
              inputmode="numeric"
              maxlength="30"
              :placeholder="$t('wallet.accountNumberPlaceholder')"
              autocomplete="off"
            >
          </label>
        </template>

        <div v-else class="wallet-form__field wallet-form__field--wallet-check">
          <span class="wallet-form__label">{{ $t('wallet.tetherAddressLabel') }} <em>({{ $t('wallet.required') }})</em></span>
          <div class="wallet-wallet-check">
            <input
              v-model.trim="tetherAddress"
              type="text"
              maxlength="64"
              class="wallet-wallet-check__input"
              :placeholder="$t('wallet.tetherAddressPlaceholder')"
              autocomplete="off"
              :disabled="walletCheckLoading"
              @input="onTetherAddressInput"
            >
            <button
              type="button"
              class="wallet-wallet-check__btn"
              :disabled="walletCheckLoading || !tetherAddress"
              @click="checkWalletBalance"
            >
              {{ walletCheckLoading ? $t('wallet.checking') : $t('wallet.walletCheck') }}
            </button>
            <span
              v-if="walletCheckVerified"
              class="wallet-wallet-check__balance"
            >
              <span class="wallet-wallet-check__balance-label">{{ $t('wallet.walletBalanceLabel') }}</span>
              <strong class="wallet-wallet-check__balance-value">{{ walletUsdtBalanceDisplay }}</strong>
              <span class="wallet-wallet-check__balance-unit">USDT</span>
            </span>
          </div>
          <p v-if="walletCheckError" class="wallet-wallet-check__error" role="alert">{{ walletCheckError }}</p>
          <p v-else-if="!walletCheckVerified" class="wallet-wallet-check__hint">
            {{ $t('wallet.walletCheckHintWithdraw') }}
          </p>
        </div>

        <div class="wallet-form__actions">
          <button
            type="submit"
            class="wallet-form__submit"
            :disabled="submitting || (withdrawTab === 'tether' && !walletCheckVerified)"
          >
            {{ submitting ? $t('wallet.requesting') : $t('wallet.withdrawRequest') }}
          </button>
          <button type="button" class="wallet-form__ghost" :disabled="submitting" @click="$emit('support')">
            {{ $t('wallet.supportInquiry') }}
          </button>
        </div>
      </form>
    </article>

    <article class="wallet-panel wallet-panel--history">
      <div class="wallet-panel__title-row">
        <h3 class="wallet-panel__title">{{ $t('wallet.withdrawHistoryTitle') }}</h3>
        <button
          type="button"
          class="wallet-panel__title-action"
          :disabled="historyLoading"
          :aria-label="$t('wallet.historyReloadWithdraw')"
          @click="reloadHistory"
        >
          <svg
            class="wallet-panel__title-action-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <p class="wallet-panel__desc">{{ $t('wallet.historyDesc') }}</p>

      <p v-if="historyLoading" class="wallet-section__status">{{ $t('wallet.loadingWithdrawHistory') }}</p>
      <p v-else-if="historyError" class="wallet-section__status wallet-section__status--error">{{ historyError }}</p>
      <p v-else-if="!historyRows.length" class="wallet-section__status">{{ $t('wallet.emptyWithdrawHistory') }}</p>

      <div v-else class="wallet-table-wrap">
        <table class="wallet-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('wallet.colRequestDate') }}</th>
              <th scope="col">{{ $t('wallet.colType') }}</th>
              <th scope="col" class="wallet-table__num">{{ $t('wallet.colAmount') }}</th>
              <th scope="col">{{ $t('wallet.colWithdrawInfo') }}</th>
              <th scope="col">{{ $t('wallet.colMemo') }}</th>
              <th scope="col">{{ $t('wallet.colStatus') }}</th>
              <th scope="col">{{ $t('wallet.colProcessedDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in historyRows" :key="row.idx">
              <td>{{ formatUtcDateTime(row.regdate) }}</td>
              <td>
                <span class="wallet-type" :class="withdrawTypeClass(row.type)">
                  {{ withdrawTypeLabel(row.type) }}
                </span>
              </td>
              <td class="wallet-table__num">{{ formatCurrency(Math.abs(Number(row.amount) || 0)) }}</td>
              <td>{{ formatWithdrawAccountInfo(row) }}</td>
              <td class="wallet-table__memo">{{ displayValue(row.memo) }}</td>
              <td>
                <span class="wallet-status" :class="withdrawStatusClass(row.status)">
                  {{ withdrawStatusLabel(row.status) }}
                </span>
              </td>
              <td>{{ formatWithdrawProcDate(row) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="historyTotal > 0" class="wallet-pagination">
        <button
          type="button"
          class="wallet-pagination__btn"
          :disabled="historyPage <= 1 || historyLoading"
          @click="goHistoryPage(historyPage - 1)"
        >
          {{ $t('common.prev') }}
        </button>
        <span class="wallet-pagination__info">{{ historyPage }} / {{ historyTotalPages }}</span>
        <button
          type="button"
          class="wallet-pagination__btn"
          :disabled="historyPage >= historyTotalPages || historyLoading"
          @click="goHistoryPage(historyPage + 1)"
        >
          {{ $t('common.next') }}
        </button>
      </footer>
    </article>

    <BlackGoldWithdrawAvailableModal
      :visible="withdrawAvailableModalOpen"
      :available-amount="availableBalanceDisplay"
      :balance-loading="balanceLoading"
      @close="closeWithdrawAvailableModal"
      @refresh="reloadBalance"
    >
      <div class="wallet-withdraw-detail">
        <div class="wallet-balance-breakdown" role="note" :aria-label="$t('wallet.amountInfoAria')">
          <div class="wallet-balance-breakdown__head">
            <span class="wallet-balance-breakdown__title">{{ $t('wallet.amountInfoTitle') }}</span>
          </div>
          <div class="wallet-balance-breakdown__row">
            <span>{{ $t('wallet.totalDeposit') }}</span>
            <strong>{{ totalChargeDisplay }}</strong>
          </div>
          <div class="wallet-balance-breakdown__row">
            <span>{{ $t('wallet.totalBet') }}</span>
            <strong>{{ totalBetDisplay }}</strong>
          </div>
          <div class="wallet-balance-breakdown__row">
            <span>{{ $t('wallet.requiredBet', { percent: withdrawLimitPercentDisplay }) }}</span>
            <strong>{{ requiredBetDisplay }}</strong>
          </div>
          <div class="wallet-balance-breakdown__row">
            <span>{{ $t('wallet.heldMoney') }}</span>
            <strong>{{ moneyBalanceDisplay }}</strong>
          </div>
          <div v-if="!withdrawBetRequirementMet" class="wallet-balance-breakdown__row">
            <span>{{ $t('wallet.heldBonus') }}</span>
            <strong>{{ bonusBalanceDisplay }}</strong>
          </div>
          <div class="wallet-balance-breakdown__formula">
            <span>{{ $t('wallet.withdrawRule') }}</span>
            <strong>{{ withdrawRuleSummary }}</strong>
          </div>
          <div class="wallet-balance-breakdown__formula">
            <span>{{ $t('wallet.formula') }}</span>
            <strong>{{ withdrawFormulaDisplay }}</strong>
          </div>
        </div>

        <div class="wallet-withdraw-rules">
          <h4 class="wallet-withdraw-rules__title">{{ $t('wallet.rulesTitle') }}</h4>
          <ul class="wallet-withdraw-rules__notes">
            <li>
              <strong>{{ $t('wallet.totalDeposit') }}</strong>
              {{ $t('wallet.ruleTotalDepositNote') }}
            </li>
            <li>
              <strong>{{ $t('wallet.totalBet') }}</strong>
              {{ $t('wallet.ruleTotalBetNote') }}
            </li>
          </ul>
          <ul class="wallet-withdraw-rules__list">
            <li>
              <strong>{{ $t('wallet.ruleBetMetTitle') }}</strong>
              {{ $t('wallet.ruleBetMetDesc', { percent: withdrawLimitPercentDisplay }) }}
            </li>
            <li>
              <strong>{{ $t('wallet.ruleBetNotMetTitle') }}</strong>
              {{ $t('wallet.ruleBetNotMetDesc') }}
            </li>
          </ul>
          <p
            class="wallet-withdraw-rules__status"
            :class="withdrawBetRequirementMet ? 'wallet-withdraw-rules__status--ok' : 'wallet-withdraw-rules__status--limited'"
          >
            {{ withdrawStatusMessage }}
          </p>
        </div>
      </div>
    </BlackGoldWithdrawAvailableModal>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import WalletUsdtQuote from '@/components/black-gold/WalletUsdtQuote.vue'
import BlackGoldWithdrawAvailableModal from '@/components/black-gold/BlackGoldWithdrawAvailableModal.vue'
import { AuthService, GameService, WalletService, SystemService } from '@/services'
import { getBithumbUsdtRateState } from '@/services/bithumbRate'
import { getLoginId } from '@/utils/auth'
import { getCookie } from '@/utils/cookie'
import { formatMoney, formatUtcDateTime, displayValue } from '@/utils/format'
import { clearBgModalScrollLock } from '@/utils/scrollLock'
import {
  MIN_WITHDRAW_AMOUNT,
  WITHDRAW_LIST_PAGE_SIZE,
  normalizeWithdrawList,
  resolveWithdrawRequestError,
  withdrawStatusLabel,
  withdrawStatusClass,
  withdrawTypeLabel,
  withdrawTypeClass,
  formatWithdrawProcDate,
  buildWithdrawRequestMemo,
  computeTotalBet,
  computeWithdrawBetThreshold,
  isWithdrawBetRequirementMet,
  computeWithdrawableBalance
} from '@/utils/wallet'
import {
  resolvePaymentMethod,
  paymentMethodAllowsBank,
  paymentMethodAllowsTether,
  normalizeWalletTab,
  canSelectWalletTab,
  getWithdrawSubtitle
} from '@/utils/paymentMethod'
import {
  fetchTrc20UsdtBalance,
  formatTrc20UsdtBalance,
  isValidTronAddress
} from '@/utils/tronUsdt'

export default {
  name: 'BlackGoldWithdrawPage',
  components: { WalletUsdtQuote, BlackGoldWithdrawAvailableModal },
  data () {
    return {
      withdrawTab: 'bank',
      amount: 0,
      amountDisplay: '',
      bankName: '',
      bankMaster: '',
      bankAccount: '',
      tetherAddress: '',
      availableBalance: null,
      bonusBalance: null,
      totalCharge: null,
      totalBet: null,
      withdrawLimitPercent: null,
      balanceLoading: false,
      submitting: false,
      submitError: '',
      successMessage: '',
      historyLoading: false,
      historyError: '',
      historyRows: [],
      historyTotal: 0,
      historyPage: 1,
      walletCheckLoading: false,
      walletCheckVerified: false,
      walletUsdtBalance: null,
      walletCheckError: '',
      withdrawAvailableModalOpen: false
    }
  },
  computed: {
    ...mapState({
      siteInfo: state => state.siteInfo
    }),
    paymentMethod () {
      return resolvePaymentMethod(this.siteInfo)
    },
    showBankWalletTab () {
      return paymentMethodAllowsBank(this.paymentMethod)
    },
    showTetherWalletTab () {
      return paymentMethodAllowsTether(this.paymentMethod)
    },
    showWalletTabs () {
      return this.showBankWalletTab && this.showTetherWalletTab
    },
    withdrawSubtitle () {
      return getWithdrawSubtitle(this.paymentMethod)
    },
    availableBalanceDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.withdrawableBalance == null) return '-'
      return this.formatCurrency(this.withdrawableBalance)
    },
    moneyBalanceDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.availableBalance == null) return '-'
      return this.formatCurrency(this.availableBalance)
    },
    bonusBalanceDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.bonusBalance == null) return '-'
      return this.formatCurrency(this.bonusBalance)
    },
    totalChargeDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.totalCharge == null) return '-'
      return this.formatCurrency(this.totalCharge)
    },
    totalBetDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.totalBet == null) return '-'
      return this.formatCurrency(this.totalBet)
    },
    withdrawLimitPercentDisplay () {
      if (this.balanceLoading) return '...'
      if (this.withdrawLimitPercent == null) return '-'
      const value = Number(this.withdrawLimitPercent)
      return Number.isFinite(value) ? `${value}%` : '-'
    },
    requiredBetAmount () {
      if (this.totalCharge == null || this.withdrawLimitPercent == null) return null
      return computeWithdrawBetThreshold(this.totalCharge, this.withdrawLimitPercent)
    },
    requiredBetDisplay () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.requiredBetAmount == null) return '-'
      return this.formatCurrency(this.requiredBetAmount)
    },
    withdrawBetRequirementMet () {
      if (this.totalCharge == null || this.totalBet == null || this.withdrawLimitPercent == null) return false
      return isWithdrawBetRequirementMet(this.totalCharge, this.totalBet, this.withdrawLimitPercent)
    },
    withdrawableBalance () {
      return computeWithdrawableBalance({
        balance: this.availableBalance,
        bonus: this.bonusBalance,
        totalCharge: this.totalCharge,
        totalBet: this.totalBet,
        withdrawLimitPercent: this.withdrawLimitPercent
      })
    },
    withdrawRuleSummary () {
      if (this.balanceLoading) return this.$t('common.loading')
      if (this.withdrawableBalance == null) return this.$t('wallet.ruleInfoLoading')
      if (this.withdrawBetRequirementMet) {
        return this.$t('wallet.ruleMetSummary')
      }
      return this.$t('wallet.ruleNotMetSummary')
    },
    withdrawFormulaDisplay () {
      if (this.withdrawableBalance == null) return this.$t('wallet.formulaCalculating')
      if (this.withdrawBetRequirementMet) {
        return `${this.formatCurrency(this.availableBalance)} = ${this.formatCurrency(this.withdrawableBalance)}`
      }
      return `${this.formatCurrency(this.availableBalance)} - ${this.formatCurrency(this.bonusBalance)} = ${this.formatCurrency(this.withdrawableBalance)}`
    },
    withdrawStatusMessage () {
      if (this.balanceLoading) return this.$t('wallet.conditionChecking')
      if (this.withdrawableBalance == null) return this.$t('wallet.conditionLoadFail')
      if (this.withdrawBetRequirementMet) {
        return this.$t('wallet.conditionMet', {
          totalBet: this.formatCurrency(this.totalBet),
          requiredBet: this.formatCurrency(this.requiredBetAmount)
        })
      }
      return this.$t('wallet.conditionNotMet', {
        totalBet: this.formatCurrency(this.totalBet),
        requiredBet: this.formatCurrency(this.requiredBetAmount)
      })
    },
    walletUsdtBalanceDisplay () {
      if (!this.walletCheckVerified || this.walletUsdtBalance == null) return '-'
      return formatTrc20UsdtBalance(this.walletUsdtBalance)
    },
    historyPageSize () {
      return WITHDRAW_LIST_PAGE_SIZE
    },
    historyTotalPages () {
      return Math.max(1, Math.ceil(this.historyTotal / this.historyPageSize))
    }
  },
  watch: {
    siteInfo: {
      immediate: true,
      handler () {
        this.syncWithdrawTabWithPaymentMethod()
      }
    }
  },
  mounted () {
    this.syncWithdrawTabWithPaymentMethod()
    this.prefillBankFromCookies()
    this.loadBalance()
    this.loadWithdrawHistory()
  },
  beforeDestroy () {
    clearBgModalScrollLock()
  },
  methods: {
    formatMoney,
    formatUtcDateTime,
    displayValue,
    withdrawStatusLabel,
    withdrawStatusClass,
    withdrawTypeLabel,
    withdrawTypeClass,
    formatWithdrawProcDate,
    syncWithdrawTabWithPaymentMethod () {
      this.withdrawTab = normalizeWalletTab(this.paymentMethod, this.withdrawTab)
    },
    openWithdrawAvailableModal () {
      this.withdrawAvailableModalOpen = true
    },
    closeWithdrawAvailableModal () {
      this.withdrawAvailableModalOpen = false
      clearBgModalScrollLock()
    },
    releasePageScrollLock () {
      this.withdrawAvailableModalOpen = false
      clearBgModalScrollLock()
    },
    setWithdrawTab (tab) {
      if (!canSelectWalletTab(this.paymentMethod, tab)) return
      if (this.withdrawTab === tab) return
      this.withdrawTab = tab
      this.submitError = ''
      if (tab === 'tether') {
        this.resetWalletCheck()
      }
    },
    resetWalletCheck () {
      this.walletCheckLoading = false
      this.walletCheckVerified = false
      this.walletUsdtBalance = null
      this.walletCheckError = ''
    },
    onTetherAddressInput () {
      this.resetWalletCheck()
      this.submitError = ''
    },
    async checkWalletBalance () {
      const address = String(this.tetherAddress || '').trim()
      if (!address) {
        this.walletCheckError = this.$t('wallet.tetherAddressRequired')
        return
      }
      if (!isValidTronAddress(address)) {
        this.walletCheckVerified = false
        this.walletUsdtBalance = null
        this.walletCheckError = this.$t('wallet.invalidTronAddress')
        return
      }

      this.walletCheckLoading = true
      this.walletCheckError = ''
      this.walletCheckVerified = false
      this.walletUsdtBalance = null
      try {
        const balance = await fetchTrc20UsdtBalance(address)
        this.walletUsdtBalance = balance
        this.walletCheckVerified = true
      } catch (err) {
        this.walletCheckVerified = false
        this.walletUsdtBalance = null
        this.walletCheckError = (err && err.message)
          ? err.message
          : this.$t('wallet.walletBalanceFail')
      } finally {
        this.walletCheckLoading = false
      }
    },
    prefillBankFromCookies () {
      if (!this.bankName) {
        this.bankName = String(getCookie('BankName') || '').trim()
      }
      if (!this.bankMaster) {
        this.bankMaster = String(getCookie('BankMaster') || '').trim()
      }
      if (!this.bankAccount) {
        this.bankAccount = String(getCookie('BankAccount') || '').trim()
      }
    },
    formatWithdrawAccountInfo (row) {
      if (!row) return '-'
      const account = this.displayValue(row.bank_account)
      const name = String(row.bank_name || '').trim()
      const master = String(row.bank_master || '').trim()
      if (name || master) {
        const parts = []
        if (name) parts.push(name)
        if (master) parts.push(master)
        parts.push(account !== '-' ? account : '')
        return parts.filter(Boolean).join(' / ') || account
      }
      return account
    },
    reload () {
      this.loadBalance()
      this.loadWithdrawHistory()
    },
    reloadBalance () {
      this.loadBalance()
    },
    reloadHistory () {
      this.loadWithdrawHistory()
    },
    goHistoryPage (page) {
      if (page < 1 || page > this.historyTotalPages || page === this.historyPage) return
      this.historyPage = page
      this.loadWithdrawHistory()
    },
    async loadWithdrawHistory () {
      this.historyLoading = true
      this.historyError = ''
      try {
        const res = await WalletService.withdrawList({
          offset: (this.historyPage - 1) * this.historyPageSize,
          limit: this.historyPageSize,
          loginid: getLoginId()
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.historyRows = normalizeWithdrawList(body.data)
          this.historyTotal = Number(body.data.total) || this.historyRows.length
        } else {
          this.historyRows = []
          this.historyTotal = 0
          this.historyError = (body && body.message)
            ? body.message
            : this.$t('wallet.loadWithdrawHistoryError')
        }
      } catch (err) {
        this.historyRows = []
        this.historyTotal = 0
        this.historyError = this.$t('wallet.loadWithdrawHistoryError')
      } finally {
        this.historyLoading = false
      }
    },
    async loadBalance () {
      this.balanceLoading = true
      try {
        const [runtimeRes, userRes, siteRes] = await Promise.all([
          GameService.getBalanceApi(),
          AuthService.getUserInfo(),
          SystemService.getSiteSettingInfo()
        ])
        const runtimeBody = runtimeRes && runtimeRes.data ? runtimeRes.data : null
        const userBody = userRes && userRes.data ? userRes.data : null
        const siteBody = siteRes && siteRes.data ? siteRes.data : null
        this.availableBalance = (runtimeBody && runtimeBody.code === 0 && runtimeBody.data)
          ? Number(runtimeBody.data.balance)
          : null
        if (userBody && userBody.code === 0 && userBody.data) {
          const user = userBody.data
          this.bonusBalance = Number(user.bonus) || 0
          this.totalCharge = Number(user.total_charge) || 0
          this.totalBet = computeTotalBet(user.sbet, user.cbet)
        } else {
          this.bonusBalance = null
          this.totalCharge = null
          this.totalBet = null
        }
        if (siteBody && siteBody.code === 0 && siteBody.data) {
          this.withdrawLimitPercent = Number(siteBody.data.withdraw_limit_percent)
          this.$store.commit('SET_SITE_INFO', siteBody.data)
          this.syncWithdrawTabWithPaymentMethod()
        } else {
          this.withdrawLimitPercent = null
        }
      } catch (err) {
        this.availableBalance = null
        this.bonusBalance = null
        this.totalCharge = null
        this.totalBet = null
        this.withdrawLimitPercent = null
      } finally {
        this.balanceLoading = false
      }
    },
    onAmountInput () {
      const digits = String(this.amountDisplay || '').replace(/[^\d]/g, '')
      this.amount = digits ? Number(digits) : 0
      this.amountDisplay = digits ? formatMoney(this.amount) : ''
    },
    addAmount (value) {
      this.amount = (Number(this.amount) || 0) + Number(value)
      this.amountDisplay = formatMoney(this.amount)
    },
    resetAmount () {
      this.amount = 0
      this.amountDisplay = ''
    },
    resolveTetherBankMeta () {
      return {
        bankName: String(getCookie('BankName') || '').trim() || 'USDT',
        bankMaster: String(getCookie('BankMaster') || '').trim() || 'Tether'
      }
    },
    validateWithdrawAmount () {
      if (!this.amount || this.amount < MIN_WITHDRAW_AMOUNT) {
        this.submitError = this.$t('wallet.withdrawMinAmount', { amount: this.formatCurrency(MIN_WITHDRAW_AMOUNT) })
        return false
      }
      if (this.withdrawableBalance == null) {
        this.submitError = this.$t('wallet.withdrawBalanceLoading')
        return false
      }
      if (this.amount > this.withdrawableBalance) {
        this.submitError = this.$t('wallet.withdrawExceedAvailable', { amount: this.formatCurrency(this.withdrawableBalance) })
        return false
      }
      return true
    },
    onSubmitWithdraw () {
      if (this.withdrawTab === 'bank') {
        this.submitBankWithdraw()
      } else {
        this.submitTetherWithdraw()
      }
    },
    async submitBankWithdraw () {
      this.submitError = ''
      this.successMessage = ''

      if (!this.validateWithdrawAmount()) return

      const bankName = String(this.bankName || '').trim()
      const bankMaster = String(this.bankMaster || '').trim()
      const bankAccount = String(this.bankAccount || '').trim()

      if (!bankName) {
        this.submitError = this.$t('wallet.bankNameRequired')
        return
      }
      if (!bankMaster) {
        this.submitError = this.$t('wallet.holderRequired')
        return
      }
      if (!bankAccount) {
        this.submitError = this.$t('wallet.accountRequired')
        return
      }

      this.submitting = true
      try {
        const res = await WalletService.withdrawRequest({
          amount: this.amount,
          bankAccount,
          bankName,
          bankMaster,
          memo: ''
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          const apiMessage = String(body.message || '').trim()
          this.successMessage = (apiMessage && apiMessage.indexOf('{') < 0)
            ? apiMessage
            : this.$t('wallet.withdrawSuccess')
          this.resetAmount()
          this.historyPage = 1
          await this.loadBalance()
          this.loadWithdrawHistory()
          this.$emit('success')
        } else {
          this.submitError = resolveWithdrawRequestError(body)
        }
      } catch (err) {
        this.submitError = this.$t('wallet.withdrawFail')
      } finally {
        this.submitting = false
        this.releasePageScrollLock()
      }
    },
    async submitTetherWithdraw () {
      this.submitError = ''
      this.successMessage = ''

      if (!this.validateWithdrawAmount()) return

      const bankAccount = String(this.tetherAddress || '').trim()
      if (!bankAccount) {
        this.submitError = this.$t('wallet.tetherAddressRequired')
        return
      }
      if (!isValidTronAddress(bankAccount)) {
        this.submitError = this.$t('wallet.invalidTronAddress')
        return
      }
      if (!this.walletCheckVerified) {
        this.submitError = this.$t('wallet.withdrawWalletCheckRequired')
        return
      }

      const meta = this.resolveTetherBankMeta()
      const memo = buildWithdrawRequestMemo(this.amount, getBithumbUsdtRateState().rate)
      if (!memo) {
        this.submitError = this.$t('wallet.depositRateLoading')
        return
      }

      this.submitting = true
      try {
        const res = await WalletService.withdrawRequest({
          amount: this.amount,
          bankAccount,
          bankName: meta.bankName,
          bankMaster: meta.bankMaster,
          memo
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          const apiMessage = String(body.message || '').trim()
          this.successMessage = (apiMessage && apiMessage.indexOf('{') < 0)
            ? apiMessage
            : this.$t('wallet.withdrawSuccess')
          this.resetAmount()
          this.tetherAddress = ''
          this.resetWalletCheck()
          this.historyPage = 1
          await this.loadBalance()
          this.loadWithdrawHistory()
          this.$emit('success')
        } else {
          this.submitError = resolveWithdrawRequestError(body)
        }
      } catch (err) {
        this.submitError = this.$t('wallet.withdrawFail')
      } finally {
        this.submitting = false
        this.releasePageScrollLock()
      }
    }
  }
}
</script>
